import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Users, Smartphone, Terminal, FileKey, CreditCard, ArrowRight } from 'lucide-react';
import { Modal } from '../ui/Modal.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchUsers } from '../../api/users.js';
import { fetchDevices } from '../../api/devices.js';
import { fetchCommands } from '../../api/commands.js';
import { fetchSnapshots } from '../../api/snapshots.js';
import { fetchSubscriptions } from '../../api/system.js';

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalSearchModal({ isOpen, onClose }: GlobalSearchModalProps) {
  const [query, setQuery] = useState('');
  const { t } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  const q = query.trim().toLowerCase();
  const isSearching = Boolean(isOpen && q.length > 0);

  const { data: usersData } = useQuery({
    queryKey: ['global-search-users', q],
    queryFn: () => fetchUsers({ search: q, limit: 5 }),
    enabled: isSearching,
  });

  const { data: devicesData } = useQuery({
    queryKey: ['global-search-devices', q],
    queryFn: () => fetchDevices({ search: q, limit: 5 }),
    enabled: isSearching,
  });

  const { data: commandsData } = useQuery({
    queryKey: ['global-search-commands'],
    queryFn: () => fetchCommands({ limit: 20 }),
    enabled: isSearching,
  });

  const { data: snapshotsData } = useQuery({
    queryKey: ['global-search-snapshots'],
    queryFn: () => fetchSnapshots(),
    enabled: isSearching,
  });

  const { data: subscriptionsData } = useQuery({
    queryKey: ['global-search-subscriptions'],
    queryFn: () => fetchSubscriptions(),
    enabled: isSearching,
  });

  // Search logic from real backend data
  const matchedUsers = q
    ? (usersData?.items || []).filter(
        (u) => u.id.toLowerCase().includes(q) || (u.phone_number && u.phone_number.includes(q))
      )
    : [];
  const matchedDevices = q
    ? (devicesData?.items || []).filter(
        (d) =>
          d.id.toLowerCase().includes(q) ||
          d.app_version.toLowerCase().includes(q) ||
          d.device_model?.toLowerCase().includes(q)
      )
    : [];
  const matchedCommands = q
    ? (commandsData?.items || []).filter(
        (c) => c.id.toLowerCase().includes(q) || c.type.toLowerCase().includes(q)
      )
    : [];
  const matchedSnapshots = q
    ? (snapshotsData || []).filter(
        (s) => s.id.toLowerCase().includes(q) || s.device_id.toLowerCase().includes(q)
      )
    : [];
  const matchedSubscriptions = q
    ? (subscriptionsData || []).filter(
        (s) =>
          s.id.toLowerCase().includes(q) ||
          s.external_subscription_id.toLowerCase().includes(q) ||
          s.product_id.toLowerCase().includes(q)
      )
    : [];


  const hasResults =
    matchedUsers.length > 0 ||
    matchedDevices.length > 0 ||
    matchedCommands.length > 0 ||
    matchedSnapshots.length > 0 ||
    matchedSubscriptions.length > 0;

  const handleSelect = (url: string) => {
    onClose();
    navigate(url);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('searchModal.title')} maxWidth="lg">
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('searchModal.placeholder')}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 dark:border-obsidian-700 bg-slate-50 dark:bg-obsidian-900 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-sans"
          />
        </div>

        <div className="max-h-[360px] overflow-y-auto space-y-4 pr-1">
          {q && !hasResults && (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400 text-sm">
              {t('searchModal.noResults')} "{query}".
            </div>
          )}

          {!q && (
            <div className="p-6 text-center text-xs text-slate-400">
              {t('searchModal.typeMinChars')}
            </div>
          )}

          {/* Group: Users */}
          {matchedUsers.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> {t('searchModal.users')} ({matchedUsers.length})
              </div>
              <div className="space-y-1">
                {matchedUsers.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleSelect(`/admin/users/${u.id}`)}
                    className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-obsidian-800 text-left text-xs transition-colors group"
                  >
                    <span className="font-mono text-indigo-600 dark:text-indigo-400">{u.id}</span>
                    <span className="text-slate-500 capitalize">{u.status}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Group: Devices */}
          {matchedDevices.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Smartphone className="w-3.5 h-3.5" /> {t('searchModal.devices')} ({matchedDevices.length})
              </div>
              <div className="space-y-1">
                {matchedDevices.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => handleSelect(`/admin/devices/${d.id}`)}
                    className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-obsidian-800 text-left text-xs transition-colors group"
                  >
                    <div>
                      <span className="font-mono text-indigo-600 dark:text-indigo-400 block">{d.id}</span>
                      <span className="text-[11px] text-slate-500">{d.device_model || d.platform} • v{d.app_version}</span>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Group: Commands */}
          {matchedCommands.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Terminal className="w-3.5 h-3.5" /> {t('searchModal.commands')} ({matchedCommands.length})
              </div>
              <div className="space-y-1">
                {matchedCommands.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleSelect(`/admin/commands`)}
                    className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-obsidian-800 text-left text-xs transition-colors"
                  >
                    <span className="font-mono text-slate-800 dark:text-slate-200">{c.id}</span>
                    <span className="text-slate-500 capitalize">{c.type} • {c.status}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Group: Snapshots */}
          {matchedSnapshots.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <FileKey className="w-3.5 h-3.5" /> {t('searchModal.snapshots')} ({matchedSnapshots.length})
              </div>
              <div className="space-y-1">
                {matchedSnapshots.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => handleSelect(`/admin/snapshots`)}
                    className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-obsidian-800 text-left text-xs transition-colors"
                  >
                    <span className="font-mono text-amber-600 dark:text-amber-400">{s.id}</span>
                    <span className="text-slate-500">Device: {s.device_id}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Group: Subscriptions */}
          {matchedSubscriptions.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5" /> {t('searchModal.subscriptions')} ({matchedSubscriptions.length})
              </div>
              <div className="space-y-1">
                {matchedSubscriptions.map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => handleSelect(`/admin/subscriptions`)}
                    className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-obsidian-800 text-left text-xs transition-colors"
                  >
                    <span className="font-mono text-slate-800 dark:text-slate-200">{sub.id}</span>
                    <span className="text-slate-500 uppercase">{sub.provider} • {sub.status}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-slate-100 dark:border-obsidian-750 text-[11px] text-slate-400 flex items-center justify-between">
          <span>{t('searchModal.pressEsc')}</span>
          <span>{t('searchModal.tip')}</span>
        </div>
      </div>
    </Modal>
  );
}
